import { useEffect, type RefObject } from 'react';

/**
 * U3 — étiquetage des tableaux denses pour le mobile.
 *
 * Sous 720 px, `table.tbl-dense` devient UNE CARTE PAR LIGNE : chaque `<td>`
 * affiche le libellé de sa colonne via `td::before { content: attr(data-l) }`.
 * Le libellé vient du `<th>` correspondant — c'est la seule façon d'avoir
 * « aucune colonne perdue » sans dupliquer le balisage pour le mobile.
 *
 * Pose `data-l` sur chaque cellule du `tbody` depuis les `<th>` du `thead`.
 * Aucune donnée, aucun contrôle, aucun attribut existant n'est modifié :
 * on ajoute uniquement un attribut de présentation.
 *
 * `deps` : dépendances de re-rendu (les lignes peuvent arriver après le premier
 * rendu — données async). Par défaut, l'effet se rejoue à CHAQUE rendu du
 * composant, comme dans `DaysheetViewer` depuis U1 ; c'est idempotent et sans
 * coût mesurable sur les tableaux concernés.
 */
export function injectDenseLabels(root: ParentNode): void {
  // `root` peut ÊTRE le tableau (composants qui n'ont pas de conteneur propre) :
  // `querySelectorAll` ne renvoie jamais la racine elle-même.
  const tables: HTMLTableElement[] = [];
  if (root instanceof Element && root.matches('table.tbl-dense')) tables.push(root as HTMLTableElement);
  tables.push(...Array.from(root.querySelectorAll<HTMLTableElement>('table.tbl-dense')));
  for (const table of tables) {
    const ths = Array.from(table.querySelectorAll<HTMLTableCellElement>('thead th')).map((th) =>
      (th.textContent || '').trim(),
    );
    if (!ths.length) continue;
    for (const tr of Array.from(table.querySelectorAll<HTMLTableRowElement>('tbody tr'))) {
      Array.from(tr.children).forEach((cell, i) => {
        if (cell instanceof HTMLElement) cell.setAttribute('data-l', ths[i] ?? '');
      });
    }
  }
}

export function useDenseTableLabels<T extends HTMLElement>(ref: RefObject<T | null>): void {
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    injectDenseLabels(root);
    // Les lignes arrivent souvent APRÈS le premier rendu (fetch). On réapplique
    // à chaque mutation de la sous-arborescence — jamais sur les attributs,
    // donc pas de boucle observe → write → observe.
    const mo = new MutationObserver(() => injectDenseLabels(root));
    mo.observe(root, { childList: true, subtree: true });
    return () => mo.disconnect();
  });
}
